import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  TablePagination,
  CircularProgress,
  IconButton,
  InputAdornment,
  Alert,
  Chip,
  Skeleton,
} from "@mui/material";
import { Search, Close, PersonAdd } from "@mui/icons-material";
import axios from "axios";
import { BASE_URL } from "../../../../services/configUrls";
import AddNewFacultyModal from "./AddNewFacultyModal";
import AddNewFacultyModalFDP from "./AddNewFacultyModalFDP";

const AddFacultyModalFDP = ({ open, onClose, bookslotId, onSuccess }) => {
  const [facultyList, setFacultyList] = useState([]);
  const [filteredFacultyList, setFilteredFacultyList] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [showAddNewFacultyModal, setShowAddNewFacultyModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const uniqueDesignations = [
    ...new Set(facultyList.map((f) => f.designation).filter(Boolean)),
  ];
  const uniqueBranches = [
    ...new Set(facultyList.map((f) => f.branch).filter(Boolean)),
  ];

  useEffect(() => {
    if (open) {
      fetchFacultyList();
      setSelectedFaculty([]);
      setSearchQuery("");
      setDesignationFilter("");
      setBranchFilter("");
      setPage(0);
      setError(null);
    }
  }, [open, bookslotId]);

  useEffect(() => {
    let filtered = [...facultyList];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (faculty) =>
          faculty.name.toLowerCase().includes(query) ||
          faculty.email.toLowerCase().includes(query) ||
          faculty.phone_no.includes(query),
      );
    }

    if (designationFilter) {
      filtered = filtered.filter(
        (faculty) => faculty.designation === designationFilter,
      );
    }

    if (branchFilter) {
      filtered = filtered.filter((faculty) => faculty.branch === branchFilter);
    }

    setFilteredFacultyList(filtered);
    setPage(0);
  }, [facultyList, searchQuery, designationFilter, branchFilter]);

  const fetchFacultyList = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BASE_URL}/event/faculty-list`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (response.data && response.data.data) {
        setFacultyList(response.data.data);
      }
    } catch (err) {
      setError("Failed to fetch faculty list. Please try again.");
      console.error("Error fetching faculty list:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFaculty = (faculty) => {
    const isSelected = selectedFaculty.some(
      (f) => f.faculty_id === faculty.faculty_id && f.email === faculty.email,
    );

    if (isSelected) {
      setSelectedFaculty(
        selectedFaculty.filter(
          (f) =>
            !(f.faculty_id === faculty.faculty_id && f.email === faculty.email),
        ),
      );
    } else {
      setSelectedFaculty([...selectedFaculty, faculty]);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const currentPageFaculty = paginatedFaculty();
      const newSelections = [...selectedFaculty];
      currentPageFaculty.forEach((faculty) => {
        const isAlreadySelected = newSelections.some(
          (f) =>
            f.faculty_id === faculty.faculty_id && f.email === faculty.email,
        );
        if (!isAlreadySelected) {
          newSelections.push(faculty);
        }
      });
      setSelectedFaculty(newSelections);
    } else {
      const currentPageFaculty = paginatedFaculty();
      setSelectedFaculty(
        selectedFaculty.filter(
          (selected) =>
            !currentPageFaculty.some(
              (current) =>
                current.faculty_id === selected.faculty_id &&
                current.email === selected.email,
            ),
        ),
      );
    }
  };

  const isFacultySelected = (faculty) => {
    return selectedFaculty.some(
      (f) => f.faculty_id === faculty.faculty_id && f.email === faculty.email,
    );
  };

  const isAllSelected = () => {
    const currentPageFaculty = paginatedFaculty();
    return (
      currentPageFaculty.length > 0 &&
      currentPageFaculty.every((faculty) => isFacultySelected(faculty))
    );
  };

  const paginatedFaculty = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredFacultyList.slice(startIndex, endIndex);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddFacultyToBookslot = async () => {
    if (selectedFaculty.length === 0) {
      setError("Please select at least one faculty member.");
      return;
    }

    if (submitting) {
      console.log("Submission already in progress, ignoring duplicate call");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const facultyIds = selectedFaculty.map((f) => f.faculty_id);
      const response = await axios.post(
        `${BASE_URL}/event/add-faculty-to-bookslot`,
        {
          bookslot_id: bookslotId,
          faculty_ids: facultyIds,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      const htmlContent = response.data.detail || "Faculty added successfully!";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const textContent =
        tempDiv.textContent || tempDiv.innerText || htmlContent;
      alert(textContent.trim());

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Failed to add faculty. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
      setSelectedFaculty([]);
      setSearchQuery("");
      setDesignationFilter("");
      setBranchFilter("");
      setError(null);
      setPage(0);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDesignationFilter("");
    setBranchFilter("");
  };

  const handleAddNewFacultySuccess = () => {
    fetchFacultyList();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1.5 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1" fontWeight="600">
              Add Faculty Members
            </Typography>
            <IconButton
              onClick={handleClose}
              disabled={submitting}
              size="small"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Button
              onClick={() => setShowAddNewFacultyModal(true)}
              variant="outlined"
              startIcon={<PersonAdd />}
              size="small"
              sx={{
                borderRadius: "8px",
                borderColor: "#2E7D32",
                color: "#2E7D32",
                "&:hover": {
                  borderColor: "#1B5E20",
                  bgcolor: "rgba(46, 125, 50, 0.04)",
                },
              }}
            >
              Add New Faculty
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Search by name, email or phone"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: "250px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Designation</InputLabel>
              <Select
                value={designationFilter}
                onChange={(e) => setDesignationFilter(e.target.value)}
                label="Designation"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueDesignations.map((designation) => (
                  <MenuItem key={designation} value={designation}>
                    {designation}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Branch</InputLabel>
              <Select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                label="Branch"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueBranches.map((branch) => (
                  <MenuItem key={branch} value={branch}>
                    {branch}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(searchQuery || designationFilter || branchFilter) && (
              <Button onClick={clearFilters} size="small">
                Clear Filters
              </Button>
            )}
          </Box>

          {selectedFaculty.length > 0 && (
            <Chip
              label={`${selectedFaculty.length} selected`}
              color="primary"
              sx={{ mb: 2 }}
            />
          )}

          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box>
              {[1, 2, 3, 4, 5].map((item) => (
                <Skeleton key={item} height={60} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selectedFaculty.length > 0 && !isAllSelected()
                          }
                          checked={isAllSelected()}
                          onChange={handleSelectAll}
                          disabled={paginatedFaculty().length === 0}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Designation</TableCell>
                      <TableCell>Branch</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedFaculty().length > 0 ? (
                      paginatedFaculty().map((faculty, index) => (
                        <TableRow
                          key={`${faculty.faculty_id}-${faculty.email}-${index}`}
                          hover
                          onClick={() => handleSelectFaculty(faculty)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isFacultySelected(faculty)}
                              onChange={() => handleSelectFaculty(faculty)}
                            />
                          </TableCell>
                          <TableCell>{faculty.name}</TableCell>
                          <TableCell>{faculty.email}</TableCell>
                          <TableCell>{faculty.phone_no}</TableCell>
                          <TableCell>{faculty.designation || "-"}</TableCell>
                          <TableCell>{faculty.branch || "-"}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {filteredFacultyList.length === 0
                            ? searchQuery || designationFilter || branchFilter
                              ? "No faculty found matching your filters"
                              : "No faculty available"
                            : "No results"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredFacultyList.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
            size="small"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleAddFacultyToBookslot}
            variant="contained"
            disabled={submitting || selectedFaculty.length === 0}
            startIcon={
              submitting ? <CircularProgress size={16} /> : <PersonAdd />
            }
            size="small"
            sx={{
              borderRadius: "8px",
              bgcolor: "#1565C0",
              "&:hover": { bgcolor: "#0D47A1" },
            }}
          >
            {submitting
              ? "Processing..."
              : `Add ${selectedFaculty.length > 0 ? `(${selectedFaculty.length})` : ""} Faculty`}
          </Button>
        </DialogActions>
      </Dialog>

      <AddNewFacultyModalFDP
        open={showAddNewFacultyModal}
        onClose={() => setShowAddNewFacultyModal(false)}
        bookslotId={bookslotId}
        onSuccess={handleAddNewFacultySuccess}
      />
    </>
  );
};

export default AddFacultyModalFDP;
